import { AuthRoutes, PrivateRoute } from '@codezo/auth';
import { AuthRoleEnum } from '@codezo/graphql';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuth } from './hooks/useAuth';
import { formatLoginUser } from './lib/format-auth-user';
import { getJobLinkToken, JOB_PATH } from './lib/job-session';
import { JobPage } from './pages/job/JobPage';
import { NotificationLinkRoute } from './pages/notifications/NotificationLinkRoute';
import { takeNotificationDestination } from './lib/notification-destination';
import { PortalGate } from './layouts/PortalGate';
import { PortalLayout } from './layouts/PortalLayout';
import { JobDetailPage } from './pages/jobs/JobDetailPage';
import { JobsPage } from './pages/jobs/JobsPage';
import { TeamPage } from './pages/team/TeamPage';
import './styles.css';

/**
 * A job session that can't be restored has no account behind it, so the public
 * login page is a dead end. Send the tab back to its job, which re-runs the
 * code challenge in place.
 *
 * Only when it was the JOB that lost its session: a lapsed portal session is an
 * account's, and belongs on the login page even in a tab that once opened a
 * link. The reauth route carries where it came from as `#prevPath=`.
 */
function resolveJobFailureRedirect(): string | null {
	const prevPath = new URLSearchParams(window.location.hash.slice(1)).get('prevPath') ?? '';
	return prevPath.startsWith(JOB_PATH) && !prevPath.startsWith('/jobs') && getJobLinkToken()
		? JOB_PATH
		: null;
}

export default function App() {
	const { isAuthenticated, user, setUser, setAccessToken, token } = useAuth();

	return (
		<>
			<Toaster position="bottom-right" />
			<Router>
				<AuthRoutes
					setUser={setUser}
					setAccessToken={setAccessToken}
					userFormatter={formatLoginUser}
					token={token}
					requiredRole={AuthRoleEnum.Vetter}
					resolveFailureRedirect={resolveJobFailureRedirect}
					resolvePendingRedirect={takeNotificationDestination}
				/>
				<Routes>
					{/*
					 * A supplier's job, opened from an emailed vetter link. Public by
					 * necessity — the supplier has no account and the emailed code is
					 * the gate. Deliberately outside any portal chrome: the session is
					 * bound to this one job and nothing else in the app is for it.
					 */}
					<Route path={JOB_PATH} element={<JobPage />} />
					{/* Notification links — bell items and email. Never move this path. */}
					<Route path="/n/:type/:id" element={<NotificationLinkRoute />} />
					{/*
					 * The portal: a supplier signed in with a password. Login itself is
					 * the public app's; it hands back through /auth/callback, which
					 * lands on /dashboard.
					 */}
					<Route
						path="/"
						element={
							<PrivateRoute
								isAuthenticated={isAuthenticated}
								user={user}
								requiredRole={AuthRoleEnum.Vetter}
							>
								<PortalGate>
									<PortalLayout />
								</PortalGate>
							</PrivateRoute>
						}
					>
						<Route index element={<Navigate to="/jobs" replace />} />
						<Route path="dashboard" element={<Navigate to="/jobs" replace />} />
						<Route path="jobs" element={<JobsPage />} />
						<Route path="jobs/:id" element={<JobDetailPage />} />
						<Route path="team" element={<TeamPage />} />
					</Route>
				</Routes>
			</Router>
		</>
	);
}
